// FD-592 (FD-ADR-047 §3) — the PKCE pair for the wave-activation browser sign-in.
//
// ── WHERE THE VERIFIER LIVES, AND WHERE IT MUST NEVER GO ───────────────────
//
// The verifier is held in COMPONENT MEMORY ONLY (a React ref in
// `useBrowserSignInActivation`). Never `localStorage`, never `sessionStorage`,
// never a URL, never a log. FD-ADR-032 §3 rejected a server-held session for the
// device-code flow because every storage option is worse than the problem, and the
// R-FD-055 amendment to FD-ADR-046 §3 accepted a browser-held verifier on the same
// reasoning: it is a redemption handle, single-use, bound to one client and one
// redirect, and worthless without a code a human produced by signing in.
//
// The cost of memory-only is that a reload loses it. That is the correct trade —
// the page says so and starts the sign-in again — and a test spies on both
// storages and on `history` to keep it true.
//
// RFC 7636: the verifier is 43–128 characters of [A-Za-z0-9-._~]; 32 random bytes
// in base64url is exactly 43, the shortest legal value with 256 bits of entropy.
// The challenge is base64url(SHA-256(verifier)), method S256 — the only method the
// Manager accepts (`Test-DrsCodeChallenge`).

export interface PkcePair {
  verifier: string;
  challenge: string;
}

/** Unpadded base64url of raw bytes. */
export function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Mirrors the Manager's `Test-DrsCodeVerifier` exactly. */
export const PKCE_VERIFIER_PATTERN = /^[A-Za-z0-9._~-]{43,128}$/;

export async function createPkcePair(): Promise<PkcePair> {
  const random = new Uint8Array(32);
  crypto.getRandomValues(random);
  const verifier = base64Url(random);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: base64Url(new Uint8Array(digest)) };
}
